import { createAction, createSelector, type ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import {
  BaseField,
  EMPTY_ARRAY,
  Field,
  getTypedKeys,
  MarketAccountStatusEnum,
  MarketAccountTypeEnum,
  ModeEnum,
  NumericField,
  QuoteStatusEnum,
  resolvePricingModeDisplaySpecs,
  toBig,
  Unit,
  validatePrecision,
  type CustomerQuote,
  type RequiredProperties,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { keyedActionWithInstanceID } from '../../../../providers/AppStateProvider/keyedReducer';
import { buildCustomerQuotePricingRequest } from '../pricingRequest';
import type { ManualRFQPricingFormHandler, ManualRFQPricingState } from '../types';
import { customerQuoteToBidAskFields, getFieldsToDisable, getPricingMethodFromQuote, isSpreadValid } from '../utils';

export type BidAskFields = {
  /** Symbol for the bid-asks - is undefined for composite bid-ask, defined for legs */
  symbol: string | undefined;
  bid: NumericField;
  ask: NumericField;
};

export type CompositeBidAskFields = BidAskFields & {
  bidQty: NumericField;
  askQty: NumericField;
};

/** BidAskFields for legs must have a symbol */
export type LegBidAskFields = RequiredProperties<BidAskFields, 'symbol'>;

export type EditableSide = keyof Pick<BidAskFields, 'bid' | 'ask'>;

export type PricingMethod = 'composite' | 'per-leg';

export interface RFQPricingFormState {
  compositeBidAsk: CompositeBidAskFields;
  legBidAsks: LegBidAskFields[];
  pricingMethod: PricingMethod;
  clearingAccountField: Field<string>;
}

export function getInitialRFQPricingFormState(): RFQPricingFormState {
  return {
    pricingMethod: 'composite',
    compositeBidAsk: {
      symbol: '',
      bid: new NumericField({ name: 'Customer Sell Price', isRequired: true, unit: Unit.Decimal }),
      ask: new NumericField({ name: 'Customer Buy Price', isRequired: true, unit: Unit.Decimal }),
      bidQty: new NumericField({ name: 'Customer Sell Qty', isRequired: false, unit: Unit.Decimal }),
      askQty: new NumericField({ name: 'Customer Buy Qty', isRequired: false, unit: Unit.Decimal }),
    },
    legBidAsks: [],
    clearingAccountField: new Field<string>({ name: 'Clearing Account', isRequired: false }),
  };
}

/** Disable all bid and ask fields. You never want to have an "enableAllInputFields", if you need to re-enable after calling this function,
 * you should use "setDisabledBasedOnQuote"
 */
const disableAllFields = (state: WritableDraft<ManualRFQPricingState>) => {
  const form = state.form;

  form.compositeBidAsk.bid = form.compositeBidAsk.bid.setDisabled(true);
  form.compositeBidAsk.ask = form.compositeBidAsk.ask.setDisabled(true);
  form.compositeBidAsk.bidQty = form.compositeBidAsk.bidQty.setDisabled(true);
  form.compositeBidAsk.askQty = form.compositeBidAsk.askQty.setDisabled(true);

  form.legBidAsks.forEach(leg => {
    leg.bid = leg.bid.setDisabled(true);
    leg.ask = leg.ask.setDisabled(true);
  });

  form.clearingAccountField = form.clearingAccountField.setDisabled(true);
};

/**
 * Given the state.currentCustomerQuote status and side, will ensure our inputs are in the correct disabled-state.
 * Used whenever the currentCustomerQuote changes.
 */
function setDisabledBasedOnQuote(state: WritableDraft<ManualRFQPricingState>) {
  const cq = state.currentCustomerQuote;
  if (cq == null) {
    disableAllFields(state);
    return;
  }

  const form = state.form;
  const sizeCounteringEnabled = cq.NegotiationParameters?.SizeCountering === ModeEnum.Enabled;
  const { disableBidFields, disableAskFields, disableOtherFields } = getFieldsToDisable(cq);

  // Bid-ask fields
  form.compositeBidAsk.bid = form.compositeBidAsk.bid
    .setDisabled(disableBidFields)
    .setIsRequired(form.pricingMethod === 'composite' && !disableBidFields);
  form.compositeBidAsk.ask = form.compositeBidAsk.ask
    .setDisabled(disableAskFields)
    .setIsRequired(form.pricingMethod === 'composite' && !disableAskFields);

  form.compositeBidAsk.bidQty = form.compositeBidAsk.bidQty
    .setDisabled(disableBidFields)
    .setIsRequired(sizeCounteringEnabled && !disableBidFields);
  form.compositeBidAsk.askQty = form.compositeBidAsk.askQty
    .setDisabled(disableAskFields)
    .setIsRequired(sizeCounteringEnabled && !disableAskFields);

  form.legBidAsks?.forEach(leg => {
    leg.bid = leg.bid
      .setDisabled(disableBidFields)
      .setIsRequired(form.pricingMethod === 'per-leg' && !disableBidFields);
    leg.ask = leg.ask
      .setDisabled(disableAskFields)
      .setIsRequired(form.pricingMethod === 'per-leg' && !disableAskFields);
  });

  // Other fields
  form.clearingAccountField = form.clearingAccountField.setDisabled(disableOtherFields);
}

function setPricingMethod(state: WritableDraft<ManualRFQPricingState>, pricingMethod: PricingMethod) {
  const form = state.form;
  form.pricingMethod = pricingMethod;

  if (pricingMethod === 'composite') {
    // Switching to composite (top-down). Reset all leg bid-asks.
    form.legBidAsks?.forEach(leg => {
      leg.bid = leg.bid.updateValue(undefined).setTouched(false).setIsRequired(false);
      leg.ask = leg.ask.updateValue(undefined).setTouched(false).setIsRequired(false);
    });

    // And make the composite bid-ask required.
    form.compositeBidAsk.bid = form.compositeBidAsk.bid.setIsRequired(true);
    form.compositeBidAsk.ask = form.compositeBidAsk.ask.setIsRequired(true);
  }

  if (pricingMethod === 'per-leg') {
    // Reset the composite bid-ask.
    form.compositeBidAsk.bid = form.compositeBidAsk.bid.updateValue(undefined).setTouched(false).setIsRequired(false);
    form.compositeBidAsk.ask = form.compositeBidAsk.ask.updateValue(undefined).setTouched(false).setIsRequired(false);

    // And make the leg bid-asks required.
    form.legBidAsks?.forEach(leg => {
      leg.bid = leg.bid.setIsRequired(true);
      leg.ask = leg.ask.setIsRequired(true);
    });
  }
}

function setCompositePrice(
  state: WritableDraft<ManualRFQPricingState>,
  value: string | undefined,
  side: EditableSide,
  updatePricingMethod = true // set to false if there are more updates to be made in the same operation
) {
  if (updatePricingMethod) {
    setPricingMethod(state, 'composite');
  }
  const form = state.form;
  form.compositeBidAsk[side] = form.compositeBidAsk[side].updateValue(value);
}

function setCompositeQty(
  state: WritableDraft<ManualRFQPricingState>,
  value: string | undefined,
  side: EditableSide,
  touch = false
) {
  const qtyFieldBySide: Record<EditableSide, 'bidQty' | 'askQty'> = {
    bid: 'bidQty',
    ask: 'askQty',
  };
  const form = state.form;
  const qtyField = qtyFieldBySide[side];
  form.compositeBidAsk[qtyField] = form.compositeBidAsk[qtyField].updateValue(value).setTouched(touch);
}

function setLegPrice(
  state: WritableDraft<ManualRFQPricingState>,
  symbol: string,
  value: string | undefined,
  side: EditableSide,
  updatePricingMethod = true // set to false if there are more updates to be made in the same operation
) {
  const form = state.form;
  const leg = form.legBidAsks?.find(leg => leg.symbol === symbol);
  if (!leg) {
    return;
  }

  if (updatePricingMethod) {
    setPricingMethod(state, 'per-leg');
  }
  leg[side] = leg[side].updateValue(value);
}

/**
 * When a new customer quote arrives, it might have prices set on it. If it does, and the price update is applicable, we go through
 * a few steps to apply it to the form. Sadly, this might overwrite changes the user is in the middle of making. However the intention of
 * this Manual RFQ form is very much that just one user should be editing it at once.
 */
function maybeUpdateFormWithPricesFromNewCustomerQuote(
  state: WritableDraft<ManualRFQPricingState>,
  quote: CustomerQuote
) {
  if (!quote.PricingMode) {
    // Nothing we can do... I also think this will always be defined, but not tackling this right now
    return;
  }
  // A new revision of the Quote has come in, and it might have relevant prices set on it. If it does, we want to write these into the input fields.
  const isActivelyBeingPriced = quote.QuoteStatus === QuoteStatusEnum.Open;
  if (!isActivelyBeingPriced) {
    // We don't care about this update.
    return;
  }

  // The setter functions are connected to fields, which call onChange with _display values_. So we need to reflect that here, too.
  // Composite pricing effectively sets the same price on every leg, so it uses leg entry display rules as well.
  const { toDisplayPrice } = resolvePricingModeDisplaySpecs({
    pricingMode: quote.PricingMode,
    security: undefined,
  }).legEntryPrice;

  setCompositePrice(state, toDisplayPrice(quote.BidPx), 'bid', false);
  setCompositePrice(state, toDisplayPrice(quote.OfferPx), 'ask', false);
  if (quote.BidQty != null) {
    setCompositeQty(state, quote.BidQty, 'bid', false);
  }
  if (quote.OfferQty != null) {
    setCompositeQty(state, quote.OfferQty, 'ask', false);
  }

  quote.LegSummary?.forEach(leg => {
    setLegPrice(state, leg.Symbol, toDisplayPrice(leg.BidPx), 'bid', false);
    setLegPrice(state, leg.Symbol, toDisplayPrice(leg.OfferPx), 'ask', false);
  });

  const form = state.form;
  form.pricingMethod = getPricingMethodFromQuote(quote);
}

function primeRFQPricingForm(state: WritableDraft<ManualRFQPricingState>, quote: CustomerQuote) {
  const form = state.form;
  const { compositeBidAsk, legBidAsks, pricingMethod } = customerQuoteToBidAskFields(quote);

  form.compositeBidAsk = compositeBidAsk;
  form.legBidAsks = legBidAsks;
  form.pricingMethod = pricingMethod;
  form.clearingAccountField = getInitialRFQPricingFormState().clearingAccountField;
  setDisabledBasedOnQuote(state);
}

function syncRFQPricingFormWithCustomerQuote(state: WritableDraft<ManualRFQPricingState>, quote: CustomerQuote) {
  maybeUpdateFormWithPricesFromNewCustomerQuote(state, quote);
  setDisabledBasedOnQuote(state);
}

function touchAllRFQPricingForm(state: WritableDraft<ManualRFQPricingState>) {
  const form = state.form;

  // Touch all base fields.
  getTypedKeys(form).forEach(key => {
    if (form[key] instanceof BaseField) {
      // @ts-expect-error - BaseField methods have trouble lining up on keyed calls
      form[key] = form[key].setTouched(true);
    }
  });

  // And then bespoke code to touch all our bid-ask nested fields.
  form.compositeBidAsk.bidQty = form.compositeBidAsk.bidQty.setTouched(true);
  form.compositeBidAsk.askQty = form.compositeBidAsk.askQty.setTouched(true);

  if (form.pricingMethod === 'composite') {
    form.compositeBidAsk.bid = form.compositeBidAsk.bid.setTouched(true);
    form.compositeBidAsk.ask = form.compositeBidAsk.ask.setTouched(true);
  } else {
    form.legBidAsks.forEach(leg => {
      leg.bid = leg.bid.setTouched(true);
      leg.ask = leg.ask.setTouched(true);
    });
  }
}

export const rfqPricingFormActions = {
  setCompositeBid: createAction<string>('manualRFQPricing/form/setCompositeBid'),
  setCompositeAsk: createAction<string>('manualRFQPricing/form/setCompositeAsk'),
  setCompositeBidQty: createAction<string>('manualRFQPricing/form/setCompositeBidQty'),
  setCompositeAskQty: createAction<string>('manualRFQPricing/form/setCompositeAskQty'),
  setClearingAccount: createAction<string | undefined>('manualRFQPricing/form/setClearingAccount'),
  setLegBid: createAction<{ symbol: string; value: string }>('manualRFQPricing/form/setLegBid'),
  setLegAsk: createAction<{ symbol: string; value: string }>('manualRFQPricing/form/setLegAsk'),
};

export function addRFQPricingFormReducers(builder: ActionReducerMapBuilder<ManualRFQPricingState>) {
  builder
    .addCase(rfqPricingFormActions.setCompositeBid, (state, action) => {
      setCompositePrice(state, action.payload, 'bid');
    })
    .addCase(rfqPricingFormActions.setCompositeAsk, (state, action) => {
      setCompositePrice(state, action.payload, 'ask');
    })
    .addCase(rfqPricingFormActions.setCompositeBidQty, (state, action) => {
      setCompositeQty(state, action.payload, 'bid', true);
    })
    .addCase(rfqPricingFormActions.setCompositeAskQty, (state, action) => {
      setCompositeQty(state, action.payload, 'ask', true);
    })
    .addCase(rfqPricingFormActions.setClearingAccount, (state, action) => {
      const form = state.form;
      form.clearingAccountField = form.clearingAccountField.updateValue(action.payload);
    })
    .addCase(rfqPricingFormActions.setLegBid, (state, action) => {
      setLegPrice(state, action.payload.symbol, action.payload.value, 'bid');
    })
    .addCase(rfqPricingFormActions.setLegAsk, (state, action) => {
      setLegPrice(state, action.payload.symbol, action.payload.value, 'ask');
    });
}

export function getRFQPricingFormActions(instanceID: string) {
  return {
    setCompositeBid: keyedActionWithInstanceID(rfqPricingFormActions.setCompositeBid, instanceID),
    setCompositeAsk: keyedActionWithInstanceID(rfqPricingFormActions.setCompositeAsk, instanceID),
    setCompositeBidQty: keyedActionWithInstanceID(rfqPricingFormActions.setCompositeBidQty, instanceID),
    setCompositeAskQty: keyedActionWithInstanceID(rfqPricingFormActions.setCompositeAskQty, instanceID),
    setClearingAccount: keyedActionWithInstanceID(rfqPricingFormActions.setClearingAccount, instanceID),
    setLegBid: keyedActionWithInstanceID(rfqPricingFormActions.setLegBid, instanceID),
    setLegAsk: keyedActionWithInstanceID(rfqPricingFormActions.setLegAsk, instanceID),
  };
}

/** Returns form selectors scoped to the provided instanceID. */
export function getRFQPricingFormSelectors(instanceID: string) {
  const selectActiveForm = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID],
    state => state.form
  );

  /** Based on the current pricing method, returns the bid-asks the user is currently using to price the Quote. */
  const selectUsedBidAsks = createSelector(
    (state: AppState) => selectActiveForm(state).compositeBidAsk,
    (state: AppState) => selectActiveForm(state).legBidAsks,
    (state: AppState) => selectActiveForm(state).pricingMethod,
    (composite, legs, pricingMethod) => {
      return pricingMethod === 'composite' ? [composite] : legs;
    }
  );

  /** Looks at the currently used bid-ask spread and checks if any bid-ask spread is crossed. */
  const selectAnySpreadError = createSelector(selectUsedBidAsks, (bidAsks): string | undefined => {
    const someCrossedSpread = bidAsks.some(
      bidAsk => !isSpreadValid({ bid: bidAsk.bid.bigValue, ask: bidAsk.ask.bigValue })
    );
    return someCrossedSpread ? 'Crossed spread' : undefined;
  });

  /** Specifically looks at the composite bid-ask spread and checks if it is crossed. */
  const selectCompositeSpreadError = createSelector(selectActiveForm, ({ compositeBidAsk }): string | undefined => {
    const { bid, ask } = compositeBidAsk;
    return isSpreadValid({ bid: bid.bigValue, ask: ask.bigValue }) ? undefined : 'Crossed spread';
  });

  /** Looks at the individual fields that are currently being used and checks that they all have valid values. */
  const selectAnyFieldError = createSelector(selectUsedBidAsks, (bidAsks): string | undefined => {
    const someInvalidField = bidAsks.some(bidAsk => bidAsk.bid.hasInvalidValue || bidAsk.ask.hasInvalidValue);
    return someInvalidField ? 'Invalid pricing field(s)' : undefined;
  });

  const selectCompositeQtyError = createSelector(
    selectActiveForm,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.OrderQty,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.MinSizeIncrement,
    ({ compositeBidAsk }, orderQty, minSizeIncrement): string | undefined => {
      const { bidQty, askQty } = compositeBidAsk;
      return (
        getQtyFieldError({ field: bidQty, orderQty, minSizeIncrement, side: 'bid' }) ??
        getQtyFieldError({ field: askQty, orderQty, minSizeIncrement, side: 'ask' })
      );
    }
  );

  const selectBidQtyError = createSelector(
    selectActiveForm,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.OrderQty,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.MinSizeIncrement,
    ({ compositeBidAsk }, orderQty, minSizeIncrement): string | undefined => {
      return getQtyFieldError({ field: compositeBidAsk.bidQty, orderQty, minSizeIncrement, side: 'bid' });
    }
  );

  const selectAskQtyError = createSelector(
    selectActiveForm,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.OrderQty,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.MinSizeIncrement,
    ({ compositeBidAsk }, orderQty, minSizeIncrement): string | undefined => {
      return getQtyFieldError({ field: compositeBidAsk.askQty, orderQty, minSizeIncrement, side: 'ask' });
    }
  );

  /** Combines the spread crossed error checking and the individual field checking to see if there is any error _at all_ in the entire form. */
  const selectBidAsksError = createSelector(
    selectAnySpreadError, // all fields are valid in combination (tuples)
    selectAnyFieldError, // all fields are valid on their own
    selectCompositeQtyError,
    (spreadResult, individualResult, compositeQtyError): string | undefined => {
      return spreadResult ?? individualResult ?? compositeQtyError;
    }
  );

  const selectClearingAccountOptions = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.ClearingMarket,
    (state: AppState) => state.referenceData.marketAccounts.marketAccountsByMarket,
    (clearingMarket, marketAccountsByMarket) => {
      if (clearingMarket == null) {
        return EMPTY_ARRAY;
      }

      return (marketAccountsByMarket.get(clearingMarket) ?? EMPTY_ARRAY)
        .filter(
          account => account.Type === MarketAccountTypeEnum.Trading && account.Status === MarketAccountStatusEnum.Active
        )
        .map(account => account.Name);
    }
  );

  return {
    selectActiveForm,
    selectUsedBidAsks,
    selectAnyFieldError,
    selectCompositeSpreadError,
    selectBidAsksError,
    selectBidQtyError,
    selectAskQtyError,
    selectClearingAccountOptions,
  };
}

// Integration with the ManualRFQPricingSlice defined here
export const rfqPricingFormHandler: ManualRFQPricingFormHandler = {
  prime: primeRFQPricingForm,
  syncWithCustomerQuote: syncRFQPricingFormWithCustomerQuote,
  touchAll: touchAllRFQPricingForm,
  buildPricingRequest: ({ quote, state }) => buildCustomerQuotePricingRequest({ quote, formState: state.form }),
  getSubmitErrorSelector: instanceID => getRFQPricingFormSelectors(instanceID).selectBidAsksError,
};

function getQtyFieldError({
  field,
  orderQty,
  minSizeIncrement,
  side,
}: {
  field: RFQPricingFormState['compositeBidAsk']['bidQty'];
  orderQty?: string;
  minSizeIncrement?: string;
  side: 'bid' | 'ask';
}): string | undefined {
  const label = side === 'bid' ? 'Bid Quantity' : 'Offer Quantity';

  if (field.isDisabled) {
    return undefined;
  }

  if (field.isRequired && !field.hasValue) {
    return `${label} is required`;
  }

  if (!field.hasValue) {
    return undefined;
  }

  const qtyValue = field.value;
  const qty = qtyValue == null ? undefined : toBig(qtyValue);
  if (qty?.lte(0)) {
    return `${label} must be greater than 0`;
  }

  if (orderQty && qty?.gt(orderQty)) {
    return `${label} cannot exceed order quantity: ${orderQty}`;
  }

  if (minSizeIncrement && qtyValue != null && !validatePrecision(minSizeIncrement, qtyValue)) {
    return `${label} min increment is ${minSizeIncrement}`;
  }

  return field.hasInvalidValue ? `Invalid ${label.toLowerCase()}` : undefined;
}
